import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Function to convert hex to RGB
const hexToRGB = (hex: string): string => {
  // Remove the hash if it exists
  hex = hex.replace('#', '');

  // Convert 3-digit hex to 6-digit
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  // Parse the hex values to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

// Add a keyframes animation for gradient movement
const buttonStyles = `
  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .gradient-hover {
    position: relative;
    overflow: hidden;
    z-index: 1;
  }

  .gradient-hover:not(.fixed-purple-button):not(.social-login-button)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg,
      rgba(var(--win11-accent-rgb), 0.3),
      rgba(var(--win11-accent-rgb), 0.7),
      rgba(var(--win11-accent-rgb), 0.3));
    background-size: 200% 200%;
    opacity: 0;
    z-index: -1;
    transition: opacity 0.3s ease;
  }

  .gradient-hover:not(.fixed-purple-button):not(.social-login-button):hover::before {
    opacity: 1;
    animation: gradientFlow 3s ease infinite;
  }
  
  /* Maintain text color contrast based on theme */
  .light-mode .gradient-hover:hover:not(.always-light-text):not(.fixed-purple-button):not(.social-login-button) {
    color: hsl(222.2 84% 4.9%) !important;
  }

  .dark .gradient-hover:hover:not(.always-light-text):not(.fixed-purple-button):not(.social-login-button) {
    color: hsl(210 40% 98%) !important;
  }
  
  /* Default styles for buttons that should always have white text */
  .gradient-hover.always-light-text:hover,
  .fixed-purple-button,
  .fixed-purple-button:hover {
    color: white !important;
  }
  
  /* Don't apply gradient to header buttons */
  .header-button.gradient-hover::before {
    display: none;
  }
`;

// Add the styles to the document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = buttonStyles;
  document.head.appendChild(styleElement);
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-win11-accent text-white shadow hover:text-white always-light-text gradient-hover",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:text-destructive-foreground always-light-text gradient-hover",
        outline:
          "border border-input bg-transparent shadow-sm hover:border-win11-accent gradient-hover",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm gradient-hover",
        ghost: "hover:bg-accent/50",
        link: "text-win11-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // Replace hardcoded purple classes with win11-accent classes
    let updatedClassName = className || '';

    if (updatedClassName.includes('bg-purple-500')) {
      updatedClassName = updatedClassName.replace('bg-purple-500', 'bg-win11-accent');
    }

    if (updatedClassName.includes('hover:bg-purple-600')) {
      updatedClassName = updatedClassName.replace('hover:bg-purple-600', 'hover:bg-win11-accent-hover');
    }

    // Check if this button should use fixed purple styling (not theme-aware)
    const isFixedPurpleButton = updatedClassName.includes('from-purple-600') && updatedClassName.includes('to-purple-700');
    if (isFixedPurpleButton) {
      updatedClassName = `${updatedClassName} fixed-purple-button`;
    }

    // Check if this is a header button (icon button with ghost variant)
    const isHeaderButton = variant === 'ghost' && size === 'icon';
    if (isHeaderButton) {
      updatedClassName = `${updatedClassName} header-button`;
    }

    // Add CSS variables for the accent colors during component rendering
    React.useEffect(() => {
      if (typeof document !== 'undefined') {
        // Get the current accent color and convert it to RGB
        const accentColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--win11-accent').trim();

        // Convert the hex to RGB and set it as a CSS variable
        const rgbValue = hexToRGB(accentColor);
        document.documentElement.style.setProperty('--win11-accent-rgb', rgbValue);

        // We'll use a document-level approach instead of trying to find specific buttons
        // This ensures the app doesn't crash while still maintaining the theme-aware styling
      }
    }, []);

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className: updatedClassName }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
